import React from "react";
import useSearch from "../hooks/useSearch";
import FoodCard from "../components/FoodCard";
import { useParams } from "react-router-dom";
import { useState } from "react";
import LoadIcon from "../img/icon/loading.gif";
import { useLanguage } from "../i18n/LanguageContext";

const Search = () => {
  const { t } = useLanguage();
  const { keyword: keywordParam, mealType: mealTypeParam } = useParams();
  const mealType = mealTypeParam === "all" ? "" : mealTypeParam;
  const keyword = keywordParam === "random" ? "" : keywordParam;

  // Display-only label for the meal type; the value itself stays English
  // because it feeds the route and the API `type` parameter.
  const mealTypeKey = `mealTypes.${mealType}`;
  const mealTypeLabel =
    mealType && t(mealTypeKey) !== mealTypeKey ? t(mealTypeKey) : mealType;

  const [pageNumber, setPageNumber] = useState(0);
  const pageSize = 8;

  // Reset the page during render when the search params change, so the
  // query below only ever fires once per param change, already on page 0.
  const [prevParams, setPrevParams] = useState({ keywordParam, mealTypeParam });
  if (
    prevParams.keywordParam !== keywordParam ||
    prevParams.mealTypeParam !== mealTypeParam
  ) {
    setPrevParams({ keywordParam, mealTypeParam });
    setPageNumber(0);
  }

  const { isLoading, error, data } = useSearch(
    keyword,
    pageNumber,
    pageSize,
    mealType
  );

  const results = data?.results ?? [];
  const totalResults = data?.totalResults ?? 0;
  const hasNextPage = (pageNumber + 1) * pageSize < totalResults;

  // Define two functions to handle pagination
  const handleNextPage = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };
  const handlePrevPage = () => {
    setPageNumber((prevPageNumber) => prevPageNumber - 1);
  };

  if (isLoading) {
    return (
      <div className="bg-gray-200 w-full p-8 items-center flex flex-col justify-center min-h-screen pt-20 md:pt-8">
        <img src={LoadIcon} alt="" />
      </div>
    );
  }

  return (
    <>
      <div className="bg-gray-200 w-full p-8 items-center flex flex-col md:justify-center md:max-h-screen pt-20 md:pt-8 min-h-screen">
        <div className="w-full pb-6 md:hidden">
          <h1 className="capitalize font-semibold text-xl">
            {keyword} / {mealTypeLabel}
          </h1>
        </div>
        {error ? (
          <p className="bg-red-600 text-white text-center py-2 px-4 rounded w-full">
            {t("common.loadError")}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4  md:px-14 w-full">
              {results.length === 0 ? (
                <p>{t("common.recipeNotFound")}</p>
              ) : (
                results.map((recipe) => (
                  <FoodCard key={recipe.id} recipe={recipe} />
                ))
              )}
            </div>
            {results.length > 0 ? (
              <div className="flex justify-end pt-6 md:pr-14 pr-2 w-full">
                <button
                  onClick={handlePrevPage}
                  disabled={pageNumber === 0}
                  className={`font-bold py-2 px-4 rounded mr-2 text-white ${
                    pageNumber === 0
                      ? "bg-gray-400 "
                      : "bg-gray-600 hover:bg-gray-700 "
                  } `}
                >
                  {t("common.prev")}
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={!hasNextPage}
                  className={`font-bold py-2 px-4 rounded mr-2 text-white ${
                    !hasNextPage
                      ? "bg-gray-400 "
                      : "bg-gray-600 hover:bg-gray-700 "
                  } `}
                >
                  {t("common.next")}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </>
  );
};

export default Search;
